import type { NeedsAttention } from "../api/types";
import { emergencyText } from "../i18n/emergencyText";

export type AttentionKind = NeedsAttention["items"][number]["kind"];

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

/**
 * The words of one Needs-attention line, shared by Home and the bell panel's attention section (batch 17, attention-bell merge
 * design §3.1, §5), so the two never drift. English chrome (ruling 2026-10-04).
 */
export const ATTENTION_WORDS: Record<AttentionKind, (n: number) => string> = {
  approvals_pending: (n) => `${plural(n, "deployment")} awaiting your approval`,
  devops_queue: (n) => `${plural(n, "deployment")} in your DevOps queue`,
  sla_at_risk: (n) => `${plural(n, "ticket")} at risk of SLA breach`,
  sla_breached: (n) => `${plural(n, "ticket")} past the SLA`,
  worklogs_rejected: (n) => `${plural(n, "worklog")} rejected by the timesheet`,
  worklogs_conflict: (n) => `${plural(n, "worklog")} in conflict with the timesheet`,
  deployment_scope_blocked: (n) => `${plural(n, "deployment")} with scope items not ready`,
  mentions_unread: (n) => `${plural(n, "mention")} of you unread`,
  agent_requests_pending: (n) => `${plural(n, "AI agent request")} waiting for your approval`,
  // Batch 17 (Emergency OTP design §6.5): links to the newest pending request's code page.
  emergency_codes_pending: (n) => emergencyText("attention", { n }),
  cr_approvals_pending: (n) => `${plural(n, "change request")} awaiting customer approval`,
  // Batch 26 (F-U1): e.g. an incident's cancellation request, waiting for the PM or PD to approve or decline it.
  approval_requests_pending: (n) => `${plural(n, "request")} waiting for your decision`,
};

/** One line's words; a kind a newer server adds before this list knows it falls back to a plain count. */
export function attentionWords(kind: AttentionKind, n: number): string {
  const words = (ATTENTION_WORDS as Partial<Record<string, (n: number) => string>>)[kind];
  return words ? words(n) : plural(n, "item");
}
